export type EnumRegistry = {
  CharacterType?: string[];
  ConflictType?: string[];
  ActType?: string[];
  BeatType?: string[];
  GenreType?: string[];
  EmotionType?: string[];
  WritingStyle?: string[];
  WritingTone?: string[];
};

type Locale = 'zh' | 'en';
type AliasGroup = 'ActType' | 'WritingTone' | 'CharacterType';

const normalizeKey = (value: any) => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

const ENUM_ALIASES: Record<AliasGroup, Record<string, string>> = {
  ActType: {
    第一幕: 'act_one',
    第二幕: 'act_two',
    第三幕: 'act_three',
    第四幕: 'act_four',
    第五幕: 'act_five',
    序幕: 'prologue',
    尾声: 'epilogue',
  },
  WritingTone: {
    强钩子短剧: 'emotional',
    克制写实: 'formal',
    文艺留白: 'empathetic',
    冷峻黑色: 'serious',
  },
  CharacterType: {
    supporting: 'helper',
    support: 'helper',
    配角: 'helper',
    辅助角色: 'helper',
    支持角色: 'helper',
  },
};

const PRESET_TONE_TO_CANONICAL: Record<string, string> = {
  restrained_realist: 'formal',
  high_hook: 'emotional',
  poetic_literary: 'empathetic',
  cold_noir: 'serious',
};

const ACT_ZH_LABELS: Record<string, string> = {
  act_one: '第一幕',
  act_two: '第二幕',
  act_three: '第三幕',
  act_four: '第四幕',
  act_five: '第五幕',
};

const BEAT_ZH_LABELS: Record<string, string> = {
  OPENING_IMAGE: '开场画面',
  THEME_STATED: '主题陈述',
  SETUP: '铺垫',
  CATALYST: '触发',
  DEBATE: '争论',
  BREAK_INTO_TWO: '入二幕',
  B_STORY: 'B线',
  FUN_AND_GAMES: '趣味',
  MIDPOINT: '中点',
  BAD_GUYS_CLOSE_IN: '逼近',
  ALL_IS_LOST: '尽失',
  DARK_NIGHT_OF_SOUL: '黑夜',
  BREAK_INTO_THREE: '入三幕',
  FINALE: '结尾',
  FINAL_IMAGE: '最终',
};

export const firstEnumValue = (values: string[] | undefined, fallback = '') =>
  Array.isArray(values) && values.length > 0 ? String(values[0] || fallback) : fallback;

export const normalizeEnumValue = (
  value: any,
  allowed: string[],
  aliasGroup?: AliasGroup
) => {
  if (value === undefined || value === null || value === '') return value;
  const raw = String(value).trim();
  if (!raw) return value;
  if (allowed.includes(raw)) return raw;

  const allowedByNorm = new Map<string, string>();
  allowed.forEach((item) => {
    allowedByNorm.set(normalizeKey(item), item);
  });
  const normalizedHit = allowedByNorm.get(normalizeKey(raw));
  if (normalizedHit) return normalizedHit;

  if (aliasGroup) {
    const aliasHit = ENUM_ALIASES[aliasGroup][normalizeKey(raw)];
    if (aliasHit && allowed.includes(aliasHit)) return aliasHit;
  }
  return value;
};

export const toneChoiceIdToCanonical = (toneChoiceId: string) => PRESET_TONE_TO_CANONICAL[String(toneChoiceId || '').trim()] || '';

export const toEnumLabel = (value: string) =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

export const enumDisplayLabel = (value: string, locale: Locale, kind?: 'act' | 'beat') => {
  if (locale === 'zh') {
    if (kind === 'act' && ACT_ZH_LABELS[value]) return ACT_ZH_LABELS[value];
    if (kind === 'beat' && BEAT_ZH_LABELS[value]) return BEAT_ZH_LABELS[value];
  }
  return toEnumLabel(value) || value;
};

export const defaultActOrder = (enums?: EnumRegistry) =>
  enums?.ActType && enums.ActType.length > 0
    ? enums.ActType
    : ['act_one', 'act_two', 'act_three', 'act_four', 'act_five'];

export const defaultBeatOrder = (enums?: EnumRegistry) =>
  enums?.BeatType && enums.BeatType.length > 0
    ? enums.BeatType
    : [
        'OPENING_IMAGE',
        'THEME_STATED',
        'SETUP',
        'CATALYST',
        'DEBATE',
        'BREAK_INTO_TWO',
        'B_STORY',
        'FUN_AND_GAMES',
        'MIDPOINT',
        'BAD_GUYS_CLOSE_IN',
        'ALL_IS_LOST',
        'DARK_NIGHT_OF_SOUL',
        'BREAK_INTO_THREE',
        'FINALE',
        'FINAL_IMAGE',
      ];
